/**
 * Art of Revival - Product Card Folder Button Integration
 * Adds folder save functionality to product cards
 */

const AORProductCardFolders = {
  init() {
    this.addFolderButtons();
    this.observeNewCards();
    
    // Update folder states when folders change
    document.addEventListener('aor:folders-updated', () => {
      this.updateAllStates();
    });
  },
  
  // Add folder buttons to all existing product cards
  addFolderButtons() {
    const cards = document.querySelectorAll('.card-wrapper, .product-card, .grid__item');
    cards.forEach(card => this.addButtonToCard(card));
  },
  
  // Add folder button to a single card
  addButtonToCard(card) {
    // Skip if button already exists
    if (card.querySelector('[data-folder-btn]')) return;
    
    // Get product data
    const productData = this.extractProductData(card);
    if (!productData) return;
    
    // Find the appropriate location (near the favorite button if it exists)
    const favoriteBtn = card.querySelector('[data-favorite-btn], .wishlist-btn, .favorite-btn');
    const container = favoriteBtn ? favoriteBtn.parentElement : this.findImageContainer(card);
    
    if (!container) return;
    
    // Create folder button
    const folderBtn = document.createElement('button');
    folderBtn.className = 'aor-folder-btn';
    folderBtn.setAttribute('data-folder-btn', '');
    folderBtn.setAttribute('aria-label', 'Save to folder');
    folderBtn.innerHTML = this.getFolderIconHTML();
    
    // Insert after favorite button if it exists, otherwise prepend to container
    if (favoriteBtn) {
      favoriteBtn.parentElement.insertBefore(folderBtn, favoriteBtn.nextSibling);
    } else {
      container.appendChild(folderBtn);
    }
    
    // Attach click handler
    folderBtn.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      AORFolderModal.open(productData);
    });
    
    // Set initial state
    this.updateButtonState(folderBtn, productData.id);
  },
  
  // Extract product data from card
  extractProductData(card) {
    const link = card.querySelector('a[href*="/products/"]');
    const image = card.querySelector('img');
    const title = card.querySelector('.card__heading, .product-title, h3');
    const price = card.querySelector('.price-item, .price');
    
    if (!link || !title) return null;
    
    const handle = link.getAttribute('href').split('/products/')[1]?.split('?')[0];
    
    return {
      id: handle, // Using handle as ID for consistency
      handle: handle,
      title: title.textContent.trim(),
      price: price ? price.textContent.trim() : '',
      image: image ? image.src : ''
    };
  },
  
  // Find image container as fallback
  findImageContainer(card) {
    return card.querySelector('.card__media, .card__inner, .product-image') || card;
  },
  
  // Get folder icon SVG
  getFolderIconHTML() {
    return `
      <svg class="aor-folder-btn__icon" width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path class="aor-folder-btn__icon-outline" d="M2.5 6.5C2.5 5.39543 3.39543 4.5 4.5 4.5H7.08579C7.351 4.5 7.60536 4.60536 7.79289 4.79289L9.20711 6.20711C9.39464 6.39464 9.649 6.5 9.91421 6.5H15.5C16.6046 6.5 17.5 7.39543 17.5 8.5V14.5C17.5 15.6046 16.6046 16.5 15.5 16.5H4.5C3.39543 16.5 2.5 15.6046 2.5 14.5V6.5Z" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round"/>
        <path class="aor-folder-btn__icon-fill" d="M2.5 6.5C2.5 5.39543 3.39543 4.5 4.5 4.5H7.08579C7.351 4.5 7.60536 4.60536 7.79289 4.79289L9.20711 6.20711C9.39464 6.39464 9.649 6.5 9.91421 6.5H15.5C16.6046 6.5 17.5 7.39543 17.5 8.5V14.5C17.5 15.6046 16.6046 16.5 15.5 16.5H4.5C3.39543 16.5 2.5 15.6046 2.5 14.5V6.5Z" fill="currentColor" opacity="0"/>
      </svg>
    `;
  },
  
  // Update button state (filled if in any folder)
  updateButtonState(button, productId) {
    const inFolder = AORFolders.isInAnyFolder(productId);
    const fillPath = button.querySelector('.aor-folder-btn__icon-fill');
    
    if (inFolder) {
      button.classList.add('is-saved');
      if (fillPath) fillPath.setAttribute('opacity', '1');
    } else {
      button.classList.remove('is-saved');
      if (fillPath) fillPath.setAttribute('opacity', '0');
    }
  },
  
  // Update all button states
  updateAllStates() {
    document.querySelectorAll('[data-folder-btn]').forEach(btn => {
      const card = btn.closest('.card-wrapper, .product-card, .grid__item');
      if (card) {
        const productData = this.extractProductData(card);
        if (productData) {
          this.updateButtonState(btn, productData.id);
        }
      }
    });
  },
  
  // Observe DOM for new product cards (infinite scroll, etc.)
  observeNewCards() {
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === 1) {
            // Check if the node itself is a card
            if (node.matches && node.matches('.card-wrapper, .product-card, .grid__item')) {
              this.addButtonToCard(node);
            }
            // Check for cards within the added node
            node.querySelectorAll && node.querySelectorAll('.card-wrapper, .product-card, .grid__item').forEach(card => {
              this.addButtonToCard(card);
            });
          }
        });
      });
    });
    
    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
  }
};

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => AORProductCardFolders.init());
} else {
  AORProductCardFolders.init();
}